import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Stack, Title, Card, Text, Loader, Center } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.ts';
import { useAuthStore } from '../auth/store.ts';

/**
 * Lands here after the OAuth callback. The backend's redirect target is
 *   /#/social-register/{provider}?success=true|false
 * (See server/src/modules/google-oauth/router.ts.)
 *
 *   success=true   →  show a toast, hydrate /api/account, bounce to /
 *   success=false  →  show an error toast, bounce to / (login modal will
 *                     show automatically if the bootstrap call returns 401)
 *
 *   provider = 'no-provider' means the backend hit the "no connection in
 *   session" branch — i.e. someone landed on /social/signup without a
 *   prior OAuth round-trip. Treat as a failure.
 */
export function SocialRegisterPage() {
  const { provider } = useParams<{ provider: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useAuthStore(s => s.setUser);
  const qc = useQueryClient();

  useEffect(() => {
    const ok = search.get('success') === 'true' && provider !== 'no-provider';
    (async () => {
      if (ok) {
        try {
          const res = await api.get('/api/account');
          if (res.status === 200) setUser(res.data);
        } catch {
          // Best-effort; the bootstrap on /  will re-try.
        }
        qc.invalidateQueries({ queryKey: ['account'] });
        notifications.show({
          message: `Signed in with ${provider}`,
          color: 'green',
        });
      } else {
        notifications.show({
          message: `Sign-in with ${provider ?? 'provider'} failed`,
          color: 'red',
        });
      }
      navigate('/', { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack gap="md">
      <Title order={2}>Signing in…</Title>
      <Card withBorder padding="lg" radius="md" maw={420}>
        <Center>
          <Stack align="center" gap="sm">
            <Loader />
            <Text size="sm" c="dimmed">Finishing {provider} sign-in…</Text>
          </Stack>
        </Center>
      </Card>
    </Stack>
  );
}
